import { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import TextLoader from 'components/ui-components/loaders/TextLoader';
import { PostcodeSelectorProvider } from './PostcodeSelectorProvider';
import {
  postcodeSelectorInitialState,
  postcodeSelectorReducer,
  setRenderAddressSelector,
  setAddressList,
  setAddressFieldsPreviouslyShown,
  setAddressFieldsShown,
} from './postcodeSelectorStateManager';
import {
  transformAddresses,
  selectedAddressIsFilled,
} from './postcodeSelectorUtils';
import PostcodeSearchField from './PostcodeSearchField';
import PostcodeActionButtons from './PostcodeActionButtons';
import AddressSelector from './AddressSelector';
import AddressFields from './AddressFields';

const PostcodeSelector = ({
  className,
  id,
  postcodeSearched,
  setPostcodeSearched,
  setPostcodeIsReset,
  setAddressValues,
  setValue,
  errors,
  setError,
  clearErrors,
  persistedData,
}) => {
  // Set up reducer, and persist certain data
  const [state, dispatch] = useReducer(
    postcodeSelectorReducer,
    // The initial arguments passed to the initialiser function
    { id, persistedData },
    // The initialiser function, which takes the initial arguments and returns the initial state
    initialArgs =>
      postcodeSelectorInitialState(initialArgs.id, initialArgs.persistedData),
  );

  const {
    loading,
    postcode,
    renderAddressSelector,
    fetchedAddresses,
    addressList,
    selectedAddress,
    showManualEntryButton,
    submittedAddress,
    addressFieldsPreviouslyShown,
    addressFieldsShown,
    addressFieldsValues,
  } = state;

  // Show address selector if postcode has been searched for
  useEffect(() => {
    // We need this check as when postcode lookup fails, 'postcodeSearched' is still true
    const postcodeIsValid = !errors?.[`${id}SearchPostcode`] && postcode;

    dispatch(
      setRenderAddressSelector(postcodeIsValid && postcodeSearched && !loading),
    );
  }, [postcodeSearched, postcode, loading]);

  // Manage rendering state of address fields:
  // Taking persisted data into account and ensuring the fields stay visible once shown even if user
  // removes data completely from checked fields
  useEffect(() => {
    dispatch(
      setAddressFieldsShown(selectedAddressIsFilled(selectedAddress, id)),
    );
  }, [selectedAddress]);

  useEffect(() => {
    if (addressFieldsShown) {
      dispatch(setAddressFieldsPreviouslyShown(true));
    }
  }, [addressFieldsShown]);

  const renderAddressFields =
    addressFieldsPreviouslyShown || addressFieldsShown;

  // Update address list when fetchedAddresses changes
  useEffect(() => {
    if (fetchedAddresses?.length > 0) {
      dispatch(setAddressList(transformAddresses(fetchedAddresses)));
    }
  }, [fetchedAddresses]);

  // Extract field values to use in form
  useEffect(() => {
    if (submittedAddress) {
      setAddressValues(submittedAddress);
    }
  }, [submittedAddress]);

  return (
    <div className={className}>
      <PostcodeSelectorProvider
        id={id}
        dispatch={dispatch}
        postcode={postcode}
        setPostcodeSearched={setPostcodeSearched}
        setPostcodeIsReset={setPostcodeIsReset}
        fetchedAddresses={fetchedAddresses}
        addressList={addressList}
        selectedAddress={selectedAddress}
        showManualEntryButton={showManualEntryButton}
        setValue={setValue}
        errors={errors}
        setError={setError}
        clearErrors={clearErrors}>
        <PostcodeSearchField />

        <PostcodeActionButtons />

        {loading && <TextLoader className="mt-20" />}

        {renderAddressSelector && <AddressSelector />}

        {renderAddressFields && (
          <AddressFields addressFieldsState={addressFieldsValues} />
        )}
      </PostcodeSelectorProvider>
    </div>
  );
};

PostcodeSelector.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string.isRequired, // To differentiate postcode selectors in same view
  postcodeSearched: PropTypes.bool.isRequired,
  setPostcodeSearched: PropTypes.func.isRequired,
  setPostcodeIsReset: PropTypes.func.isRequired,
  setAddressValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  setError: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  persistedData: PropTypes.shape({
    house_number: PropTypes.string,
    address_line_1: PropTypes.string,
    address_line_2: PropTypes.string,
    address_line_3: PropTypes.string,
    address_line_4: PropTypes.string,
    postcode: PropTypes.string,
  }),
};

export default PostcodeSelector;
